import { useCreatePath, useRecordContext, type RaRecord } from 'react-admin';
import { Link } from 'react-router-dom';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import { SectionCard } from '../../shared/Panel';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { useHasPermission } from '../PermissionGate';
import { PRE_ORDER_STAGE_COLORS, preOrderStageOf } from './preOrderStage';

/**
 * Sekcja „Etap prezlecenia” na karcie zlecenia — tylko do odczytu. Przy
 * każdym etapie data wejścia w niego (albo „—”), bieżący wyróżniony; dział
 * z prezlecenia. Przyjęcie zleceń dostaje odsyłacz do prezlecenia — tam
 * się je poprawia; FTL i Trade tylko czytają (Administracja §15).
 */
export const PreOrderStageSection = () => {
  const record = useRecordContext<RaRecord>();
  const createPath = useCreatePath();
  const canIntake = useHasPermission('ORDER_INTAKE');
  const s = preOrderStageOf(record);
  if (!s) return null;
  return (
    <SectionCard title="Etap prezlecenia">
      <Stack spacing={1} data-testid="pre-order-stage-section" data-stage={s.code}>
        <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip size="small" color={s.failed ? 'error' : PRE_ORDER_STAGE_COLORS[s.code] ?? 'default'} label={s.label} />
          <Typography variant="body2" color="text.secondary">
            {s.at ? `${s.atLabel} ${dateTimeNoSeconds(s.at)}` : s.atLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dział: {s.department ?? 'nie wskazano'}
          </Typography>
        </Stack>
        {s.failed ? <Alert severity="error">{s.detail}</Alert> : <Typography variant="body2">{s.detail}</Typography>}
        <Box
          component="ol"
          aria-label="Etapy prezlecenia"
          sx={{
            listStyle: 'none',
            p: 0,
            m: 0,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 0.5,
          }}
        >
          {s.steps.map((step) => (
            <Box
              component="li"
              key={step.code}
              data-testid={`pre-order-stage-step-${step.code}`}
              data-current={step.current ? 'true' : 'false'}
              sx={{
                borderTop: 3,
                borderColor: step.current ? 'primary.main' : step.at ? 'success.main' : 'divider',
                px: 1,
                py: 0.5,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: step.current ? 600 : 400 }}>
                {step.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {step.at ? dateTimeNoSeconds(step.at) : '—'}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Tylko do odczytu — dane prezlecenia i zapis do VIA prowadzi przyjęcie zleceń.
          {canIntake ? ' ' : ''}
          {canIntake ? (
            <Link to={createPath({ resource: 'pre-orders', type: 'show', id: s.preOrderId })}>Otwórz prezlecenie</Link>
          ) : null}
        </Typography>
      </Stack>
    </SectionCard>
  );
};
